/**
 * Curated IANA zones for the workshop timezone control (#1912).
 *
 * A full `Intl.supportedValuesOf('timeZone')` dump is unusable for a Finnish
 * workshop owner. The product's realistic market is Nordics, Baltics and the
 * rest of Europe; the current tenant value is always merged in so a stored
 * zone outside this list — including one the IANA predicate rejects — is
 * never silently replaced in the UI.
 *
 * UTC is not offered: the mirrored predicate rejects it on every runtime
 * (Node's `supportedValuesOf` omits it; browsers often list it), and a
 * workshop is never physically in UTC.
 */

export const CURATED_TIME_ZONES: readonly string[] = [
  'Europe/Helsinki',
  'Europe/Mariehamn',
  'Europe/Tallinn',
  'Europe/Riga',
  'Europe/Vilnius',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'Atlantic/Reykjavik',
  'Europe/Berlin',
  'Europe/Warsaw',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Paris',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Madrid',
  'Europe/Lisbon',
  'Europe/Rome',
  'Europe/Vienna',
  'Europe/Zurich',
  'Europe/Prague',
  'Europe/Athens',
];

export function timezoneSelectOptions(current: string): string[] {
  if (current !== '' && !CURATED_TIME_ZONES.includes(current)) {
    return [current, ...CURATED_TIME_ZONES];
  }
  return [...CURATED_TIME_ZONES];
}

/**
 * True only for a real, named IANA location zone.
 *
 * Mirrors `@vehiclespace/shared` `isValidIanaTimeZone`
 * (`packages/shared/src/time/iana-time-zone.ts`). The portal bundle does not
 * depend on `@vehiclespace/*` (same convention as `useExceptions` /
 * `useGlobalSearch`), so this copy is local — the two must stay in step.
 *
 * `undefined`, `null`, `''` and whitespace-only input are invalid.
 * Fixed-offset and POSIX identifiers that `Intl.DateTimeFormat` accepts
 * (`+02:00`, `GMT`, `EST5EDT`, …) are invalid. `UTC` is invalid: Node does
 * not list it in `supportedValuesOf` while browsers do, and a workshop is
 * never physically in UTC.
 */
export function isValidIanaTimeZone(candidate: unknown): boolean {
  return canonicalIanaTimeZone(candidate) !== undefined;
}

const UTC_ID = 'utc';

function canonicalIanaTimeZone(candidate: unknown): string | undefined {
  const value = trimmedName(candidate);
  if (value === undefined) return undefined;
  if (isUtcIdentifier(value)) return undefined;

  const zones = canonicalZoneMap();
  if (zones !== undefined) {
    return zones.get(value.toLowerCase());
  }
  return fallbackCanonical(value);
}

function trimmedName(candidate: unknown): string | undefined {
  if (typeof candidate !== 'string') return undefined;
  const trimmed = candidate.trim();
  return trimmed === '' ? undefined : trimmed;
}

function isUtcIdentifier(name: string): boolean {
  return name.toLowerCase() === UTC_ID;
}

function canonicalZoneMap(): Map<string, string> | undefined {
  if (typeof Intl.supportedValuesOf !== 'function') return undefined;
  try {
    const values = Intl.supportedValuesOf('timeZone');
    const map = new Map<string, string>();
    for (const name of values) {
      if (isUtcIdentifier(name)) continue;
      map.set(name.toLowerCase(), name);
    }
    return map;
  } catch {
    return undefined;
  }
}

function fallbackCanonical(value: string): string | undefined {
  if (isFixedOffsetOrPosixId(value)) return undefined;
  if (!value.includes('/')) return undefined;
  try {
    const resolved = new Intl.DateTimeFormat(undefined, { timeZone: value }).resolvedOptions()
      .timeZone;
    if (isUtcIdentifier(resolved) || isFixedOffsetOrPosixId(resolved)) return undefined;
    if (resolved.toLowerCase() !== value.toLowerCase()) return undefined;
    return resolved;
  } catch {
    return undefined;
  }
}

function isFixedOffsetOrPosixId(value: string): boolean {
  if (/^[+-]\d/.test(value)) return true;
  if (/^(GMT|UTC)$/i.test(value)) return true;
  if (/^[A-Za-z]{3,}[+-]?\d/.test(value)) return true;
  if (/^SystemV\//i.test(value)) return true;
  return false;
}

export function formatTimezonePreview(
  timeZone: string,
  now: Date,
  locale: string,
): { time: string; offset: string } | null {
  if (!isValidIanaTimeZone(timeZone)) return null;
  try {
    const time = new Intl.DateTimeFormat(locale, {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(now);
    const offset =
      new Intl.DateTimeFormat('en-GB', {
        timeZone,
        timeZoneName: 'shortOffset',
      })
        .formatToParts(now)
        .find((part) => part.type === 'timeZoneName')?.value ?? '';
    return { time, offset };
  } catch {
    return null;
  }
}
